"use client";

import styles from "./AppField.module.scss";

import { cn } from "@/utils/cn";

type FieldState = "valid" | "invalid";
type InputProps = React.ComponentProps<"input">;
type BaseProps = Omit<InputProps, "prefix">;

enum AppFieldAppearance {
  Fill = "fill",
  Outline = "outline",
}

enum AppFieldShape {
  Square = "square",
  Rounded = "rounded",
}

interface AppFieldProps extends BaseProps {
  label?: string;
  state?: FieldState;
  stateMessage?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  inputClassName?: string;
  appearance?: AppFieldAppearance;
  shape?: AppFieldShape;
  onValueChange?: (value: string) => void;
  shouldResizeIcon?: boolean;
}

const AppField: React.FC<AppFieldProps> = ({
  className,
  label,
  prefix,
  suffix,
  state,
  stateMessage,
  placeholder,
  appearance = AppFieldAppearance.Fill,
  shape = AppFieldShape.Rounded,
  inputClassName,
  onChange,
  onValueChange,
  shouldResizeIcon = true,
  type = "text",
  inputMode,
  onWheel,
  onKeyDown,
  ...rest
}) => {
  const isNumberInput = type === "number" || inputMode === "numeric";

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (isNumberInput) {
      e.currentTarget.blur();
    }
    onWheel?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isNumberInput && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
    }
    onKeyDown?.(e);
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-icon-resizable={shouldResizeIcon}
      data-appearance={appearance}
      data-shape={shape}
      data-state={state}
    >
      <div className={styles.contentContainer}>
        {prefix && (
          <>
            <div className={styles.prefix}>{prefix}</div>
            <div className={styles.verticalSeparator} />
          </>
        )}
        <label className={styles.fieldContainer}>
          <input
            className={cn(styles.field, inputClassName)}
            placeholder={placeholder || label}
            type={type}
            step={isNumberInput ? "any" : undefined}
            onChange={(e) => {
              onChange?.(e);
              onValueChange?.(e.target.value);
            }}
            onWheel={handleWheel}
            onKeyDown={handleKeyDown}
            inputMode={inputMode}
            {...rest}
          />
          <div className={styles.floatingLabel}>{label}</div>
        </label>
        {suffix && <div className={styles.suffix}>{suffix}</div>}
      </div>
      {stateMessage && (
        <div className={styles.stateMessage}>
          <span>{stateMessage}</span>
        </div>
      )}
    </div>
  );
};

export { AppField, AppFieldAppearance, AppFieldShape };
export type { FieldState, AppFieldProps };
